import { useEffect, useId, useRef, useState, type FormEvent } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'
import { ContactForm } from '#app/routes/mocra+/_form+/components/contacts/form.tsx'
import {
	createEmptyContactDraft,
	type ContactCandidate,
	type ContactDraft,
	type ContactFormMode,
} from '#app/routes/mocra+/_form+/components/contacts/types.ts'

type BaseProps = {
	readonly mode: ContactFormMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: ContactDraft) => void
	readonly initialValue?: ContactDraft
	readonly candidates?: readonly ContactCandidate[]
	readonly title?: string
	readonly description?: string
	readonly submitLabel?: string
	readonly cancelLabel?: string
	readonly onCancel?: () => void
	readonly onClosed?: () => void
	readonly formId?: string
	readonly excludedEmails?: readonly string[]
}

type DraftErrors = Partial<Record<keyof ContactDraft, string>>

export function ContactOverlay({
	mode,
	open,
	onOpenChange: setOpen,
	onSubmit,
	initialValue,
	candidates = [],
	title,
	description,
	submitLabel,
	cancelLabel = 'Cancel',
	onCancel,
	onClosed,
	formId,
	excludedEmails,
}: BaseProps) {
	const generatedFormId = useId().replace(/:/g, '')
	const overlayFormId = formId ?? `${generatedFormId}-contact-overlay-form`
	const [search, setSearch] = useState('')
	const [editDraft, setEditDraft] = useState<ContactDraft>(
		initialValue ?? createEmptyContactDraft(),
	)
	const [newDraft, setNewDraft] = useState<ContactDraft>(
		createEmptyContactDraft(),
	)
	const [activeView, setActiveView] = useState<'details' | 'new'>(
		mode === 'edit' ? 'details' : 'details',
	)
	const [errors, setErrors] = useState<DraftErrors>({})
	const [selectedCandidateId, setSelectedCandidateId] = useState<
		string | undefined
	>(initialValue?.id)

	const wasOpenRef = useRef(open)

	useEffect(() => {
		if (open) {
			setSearch('')
			setErrors({})
			const nextEditDraft = initialValue ?? createEmptyContactDraft()
			setEditDraft(nextEditDraft)
			setNewDraft(createEmptyContactDraft())
			setActiveView('details')
			setSelectedCandidateId(nextEditDraft.id)
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open && wasOpenRef.current && typeof onClosed === 'function') {
			onClosed()
		}
		wasOpenRef.current = open
	}, [open, onClosed])

	const handleCandidateSelect = (candidate: ContactCandidate) => {
		setEditDraft((prev) => ({
			...prev,
			id: candidate.id,
			title:
				prev.title && prev.title.trim().length > 0
					? prev.title
					: (candidate.source ?? prev.title ?? ''),
			name: candidate.name ?? prev.name,
			email: candidate.email ?? prev.email,
			phone: candidate.phone ?? prev.phone ?? '',
		}))
		setActiveView('details')
		setSelectedCandidateId(candidate.id)
		setErrors({})
	}

	const handleDraftFieldChange = (field: keyof ContactDraft, value: string) => {
		if (activeView === 'new') {
			setNewDraft((prev) => ({
				...prev,
				[field]: value,
			}))
		} else {
			setEditDraft((prev) => ({
				...prev,
				[field]: value,
			}))
		}
		setSelectedCandidateId((prev) =>
			activeView === 'details' ? prev : undefined,
		)
	}

	const validateDraft = (
		value: ContactDraft,
		view: 'details' | 'new',
	): DraftErrors => {
		if (view === 'details' && selectedCandidateId) {
			return {}
		}
		const nextErrors: DraftErrors = {}
		if (value.title.trim().length === 0) {
			nextErrors.title = 'Enter the role or responsibility.'
		}
		if (value.name.trim().length === 0) {
			nextErrors.name = 'Enter the contact name.'
		}
		if (value.email.trim().length === 0) {
			nextErrors.email = 'Enter the contact email.'
		}
		return nextErrors
	}

	const sanitizeDraft = (
		value: ContactDraft,
		view: 'details' | 'new',
	): ContactDraft => {
		return {
			id: view === 'details' ? (selectedCandidateId ?? value.id) : undefined,
			title: value.title.trim(),
			name: value.name.trim(),
			email: value.email.trim(),
			phone: value.phone.trim(),
		}
	}

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const workingDraft = activeView === 'new' ? newDraft : editDraft
		const sanitized = sanitizeDraft(workingDraft, activeView)
		const nextErrors = validateDraft(sanitized, activeView)
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
		setOpen(false)
	}

	const handleCandidateClear = () => {
		setSelectedCandidateId(undefined)
		setEditDraft((prev) => ({
			...prev,
			id: undefined,
		}))
		setErrors({})
	}

	const handleCancel = () => {
		setOpen(false)
		onCancel?.()
	}

	const titleCopy = title ?? (mode === 'add' ? 'Add contact' : 'Edit contact')
	const descriptionCopy =
		description ??
		'Search existing contacts or add a new person so this section routes to the right owner.'
	const submitCopy = submitLabel ?? (mode === 'add' ? 'Add' : 'Save')
	const currentDraft = activeView === 'new' ? newDraft : editDraft
	const currentEmail = activeView === 'details' ? editDraft.email : undefined

	const handleTabChange = (tab: 'details' | 'new' | 'search') => {
		if (tab === 'details') {
			setActiveView('details')
			setErrors({})
			setSelectedCandidateId((prev) => prev ?? editDraft.id)
			return
		}
		if (tab === 'new') {
			setActiveView('new')
			setErrors({})
			setSelectedCandidateId(undefined)
		}
	}

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={setOpen}
			title={titleCopy}
			description={descriptionCopy}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						{cancelLabel}
					</Button>
					<Button type="submit" form={overlayFormId}>
						{submitCopy}
					</Button>
				</div>
			}
			className="sm:max-w-lg"
		>
			<ContactForm
				formId={overlayFormId}
				mode={mode}
				draft={currentDraft}
				errors={errors}
				candidates={candidates}
				search={search}
				selectedCandidateId={selectedCandidateId}
				onDraftFieldChange={handleDraftFieldChange}
				onSearchChange={setSearch}
				onCandidateSelect={handleCandidateSelect}
				onCandidateClear={handleCandidateClear}
				onSubmit={handleSubmit}
				excludedEmails={excludedEmails}
				currentEmail={currentEmail}
				isOpen={open}
				onTabChange={handleTabChange}
			/>
		</ResponsiveOverlay>
	)
}
